import {
  KoobButton,
  KoobModal,
  KoobConfirmationModal,
  KoobInputSlider,
  KoobSearchBar,
} from '@koob/ui';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ExperienceExtrasModalHeader from './ExperienceExtrasModalHeader';
import ExperienceExtrasModalListItem from './ExperienceExtrasModalListItem';
import ExperienceExtrasModalSkipTransitionExtra from './ExperienceExtrasModalSkipTransitionExtra';
import ExperienceExtrasModalSkipChildrenExtra from './ExperienceExtrasModalSkipChildrenExtra';
import {
  ExperienceTypeEnum,
  ExtraRequirementEnum,
  ExtraScopeEnum,
} from '@koob/enums';
import { ExperienceExtraTabButton } from '@koob/experiences';
import { isEqual } from 'lodash';
import {
  getExtraProgramDayIndex,
  isExtraOfferedForPeriod,
  isExtraPricedForPeriod,
  isExtraStateAvailable,
} from '@koob/utils';
import { matchesSearchWords, toSearchWords } from '@koob/text-search';

// Local key rather than ExperienceTypeEnum.ALL_EXPERIENCE_TYPES: the tab key
// drives the id and data-testid of the tab button (tab-all-extras).
const ALL_TAB = 'All';

export default function ExperienceExtrasModal({
  open,
  setOpen,
  experience,
  experienceComposition,
  parentOccurrences,
  parentExtra,
  extrasScope,
  extras,
  selectedExtras,
  setSelectedExtras,
  updateExtrasForDay,
  prices,
  currency,
  onContinue,
  skipChildrenExtra,
  setSkipChildrenExtra,
  recommendationMode = false,
  readOnly = false,
  source,
  dayIndex,
  nextExperience,
  programDayIndex,
  parentTargetDayIndex,
  availabilitySearched = true,
  serviceDayIndex,
}) {
  const { t } = useTranslation('experiences');

  const [localSelectedExtras, setLocalSelectedExtras] = useState(
    Array.isArray(selectedExtras) ? selectedExtras : [],
  );
  const [search, setSearch] = useState('');
  const [maxDuration, setMaxDuration] = useState(8);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [currentTab, setCurrentTab] = useState(ALL_TAB);

  const [prevOpen, setPrevOpen] = useState(open);

  if (open !== prevOpen) {
    setPrevOpen(open);

    if (open) {
      setLocalSelectedExtras(
        Array.isArray(selectedExtras) ? selectedExtras : [],
      );
      setSearch('');
      setMaxDuration(8);
      // The modal stays mounted between openings: without this reset it would
      // reopen on the tab the user left it on.
      setCurrentTab(ALL_TAB);
    }
  }

  const appliedParentExperience = nextExperience
    ? [nextExperience]
    : [parentExtra];
  const appliedExperienceCompatibilies = Boolean(source)
    ? appliedParentExperience
    : [experience];

  const bestMatchIds = appliedExperienceCompatibilies.flatMap(item => {
    if (item?.bestMatches) {
      return item?.bestMatches.map(bestMatches => bestMatches?.id);
    } else {
      return [];
    }
  });

  const incompatibleIds = appliedExperienceCompatibilies.flatMap(item => {
    if (item?.incompatibilities) {
      return item.incompatibilities.map(
        incompatibilities => incompatibilities.id,
      );
    } else {
      return [];
    }
  });

  // Deduplicate extras by id so the same service can't appear twice in the modal.
  const uniqueExtras = Array.from(
    new Map((extras || []).map(extra => [extra.id, extra])).values(),
  );

  const formattedExtras = uniqueExtras.map(extra => {
    const isBestMatch = bestMatchIds.includes(extra.id);
    const isIncompatible = incompatibleIds.includes(extra.id);

    return {
      ...extra,
      compatibility: isBestMatch
        ? 'bestMatch'
        : isIncompatible
          ? 'incompatible'
          : 'neutral',
    };
  });

  formattedExtras?.sort((a, b) => {
    if (a.compatibility === 'bestMatch' && b.compatibility !== 'bestMatch') {
      return -1;
    }
    if (a.compatibility !== 'bestMatch' && b.compatibility === 'bestMatch') {
      return 1;
    }
    if (a.compatibility === 'neutral' && b.compatibility !== 'neutral') {
      return -1;
    }
    if (a.compatibility !== 'neutral' && b.compatibility === 'neutral') {
      return 1;
    }
    return 0;
  });

  // KD-3749 — the program day a service is checked on. The services
  // recommended towards the next experience mix global and day ones, so each
  // is read on the day it is configured on; their sub-options follow the day
  // their parent was resolved on.
  const serviceDayIndexOf = extra =>
    recommendationMode && !parentExtra
      ? getExtraProgramDayIndex(experience, extra.id)
      : serviceDayIndex;

  // KD-3386: only priceable required extras can gate the confirmation. A required
  // extra that is not distributed comes back without pricings, so it can never be
  // selected: keeping it here would disable the parent confirmation forever.
  // While prices are still loading every required extra keeps gating, otherwise
  // the modal would let the user confirm during the fetch window.
  // KD-3749: the same holds for a required extra left out on the day the modal
  // is opened for — it is not displayed, so it must not gate either.
  const allRequiredChildrenExtras = uniqueExtras.filter(
    e =>
      e.type === ExtraRequirementEnum.REQUIRED &&
      isExtraStateAvailable(e) &&
      isExtraOfferedForPeriod(prices, e.id, serviceDayIndexOf(e)),
  );

  const selectedRequiredChildrenExtras = localSelectedExtras.filter(se => {
    return (
      (se.parentExtraId ?? null) === (parentExtra?.id ?? null) &&
      se.experienceId === experience?.id &&
      se.type === ExtraRequirementEnum.REQUIRED &&
      se.scope === extrasScope
    );
  });

  const isDisabled =
    allRequiredChildrenExtras?.length > 0 &&
    selectedRequiredChildrenExtras?.length === 0;

  const hasCompletedSelection = recommendationMode
    ? localSelectedExtras.some(se => (extras || []).some(ei => ei.id === se.id))
    : allRequiredChildrenExtras?.length === 0 ||
      selectedRequiredChildrenExtras?.length === 1;

  const handleUpdateExtrasForDay = (_targetDayIndex, updateFn) => {
    setLocalSelectedExtras(current => updateFn(current));
  };

  const sortExtras = arr =>
    [...(Array.isArray(arr) ? arr : [])].sort((a, b) => {
      const aId = String(a?.id || '');
      const bId = String(b?.id || '');
      if (aId !== bId) return aId.localeCompare(bId);

      const aDay = Number(a?.dayIndex || 0);
      const bDay = Number(b?.dayIndex || 0);
      if (aDay !== bDay) return aDay - bDay;

      const aParent = String(a?.parentExtraId || '');
      const bParent = String(b?.parentExtraId || '');
      if (aParent !== bParent) return aParent.localeCompare(bParent);

      // KD-3650 — two occurrences of the same service on the same day tie on
      // everything above; without this the comparison would depend on the
      // order they happen to sit in, and flag phantom unsaved changes.
      return String(a?.occurrenceKey || '').localeCompare(
        String(b?.occurrenceKey || ''),
      );
    });

  const hasUnsavedChanges = !isEqual(
    sortExtras(localSelectedExtras),
    sortExtras(selectedExtras),
  );

  const discardOnClose = () => {
    setShowDiscardConfirm(false);
    setLocalSelectedExtras(Array.isArray(selectedExtras) ? selectedExtras : []);
    setOpen(false);
  };

  const requestClose = () => {
    if (!parentExtra && !readOnly && !recommendationMode && hasUnsavedChanges) {
      setShowDiscardConfirm(true);
      return;
    }
    setOpen(false);
  };

  const confirmChoice = () => {
    const hasChanged = hasUnsavedChanges;
    if (
      updateExtrasForDay &&
      extrasScope === ExtraScopeEnum.GLOBAL &&
      !parentExtra
    ) {
      const firstDay = dayIndex - (programDayIndex ?? 0);
      const duration = experience?.durationDays || 1;

      for (let i = 0; i < duration; i++) {
        const dIndex = firstDay + i;
        const extrasForDay = localSelectedExtras.filter(
          ex => ex.dayIndex === dIndex,
        );

        updateExtrasForDay(dIndex, currentExtras => {
          const otherExtras = currentExtras.filter(
            ex =>
              ex.experienceId !== experience?.id &&
              ex.parentExtraId !== experience?.id,
          );
          return [...otherExtras, ...extrasForDay];
        });
      }
    } else {
      setSelectedExtras(localSelectedExtras);
    }

    onContinue(hasChanged, localSelectedExtras);
    setOpen(false);
  };

  const EXTRAS_TABS = [
    { type: ExperienceTypeEnum.ACTIVITY },
    { type: ExperienceTypeEnum.TRANSFER },
    { type: ExperienceTypeEnum.EXTRA },
  ];

  const hoursOf = extra => {
    const total =
      (extra?.durationDays ?? 0) * 8 +
      (extra?.durationHours ?? 0) +
      (extra?.durationMinutes ?? 0) / 60;
    // null when there is no fixed duration (null *or* 0/0/0, both occur in DB).
    return total > 0 ? total : null;
  };
  // Same matching as the experiences list: accent-insensitive and
  // order-tolerant, so "tour ayutthaya" still finds "Ayutthaya day tour".
  const searchWords = toSearchWords(search);

  const matchesFilters = extra => {
    if (!matchesSearchWords(searchWords, [extra.name], extra.id)) return false;
    if (maxDuration < 8) {
      const hours = hoursOf(extra);
      if (hours != null && hours > maxDuration) return false;
    }
    return true;
  };

  // KD-3650 — the travellers this modal defaults its cards to. On the
  // sub-option modal that is the whole party of the parent service, occurrences
  // summed: reading the first occurrence alone announced 4 travellers on a
  // service split 4 + 2, while the cards below listed six.
  const paxSource = parentOccurrences?.length
    ? parentOccurrences
    : [experienceComposition];

  const tripAdults = paxSource.reduce(
    (acc, occurrence) => acc + (occurrence?.adults ?? 0),
    0,
  );
  const tripChildren = paxSource.reduce(
    (acc, occurrence) =>
      acc +
      (occurrence?.childrenBirthdates?.length ?? occurrence?.children ?? 0),
    0,
  );

  // KD-3650 — counts distinct services, not occurrences: a service split in
  // two still reads as one added service in the footer.
  const addedServicesCount = new Set(
    localSelectedExtras
      .filter(
        se =>
          (se.parentExtraId ?? null) === (parentExtra?.id ?? null) &&
          se.experienceId === experience?.id &&
          (extras || []).some(e => e.id === se.id),
      )
      .map(se => se.id),
  ).size;

  // The selected lines this modal is responsible for. A service included in the
  // program carries no price of its own and is not sold, so it is left out.
  const ownSelection = localSelectedExtras.filter(
    se =>
      (se.parentExtraId ?? null) === (parentExtra?.id ?? null) &&
      se.experienceId === experience?.id &&
      (extras || []).some(e => e.id === se.id) &&
      se.isIncluded !== true,
  );

  // KD-3650 — an occurrence split on a pax count that matches no price tier is
  // emitted without a price: saving it would file a line the trip cannot price,
  // and the timeline would show it with an empty amount. The hold-back follows
  // the service, not its siblings: a service with a price grid has something to
  // fix here (raise the pax, rebalance the split), while one with no grid at all
  // is unavailable on these dates, says so on its own card, and blocking the
  // save on it would just lock the modal.
  const hasUnpricedSelection = ownSelection.some(
    se => se.price == null && isExtraPricedForPeriod(prices, se.id),
  );

  // KD-3749 — a service with no pricing period on these dates is left out
  // rather than listed with a warning. One already selected stays: it has to
  // remain visible, and removable, once the dates moved away from its periods.
  // Selected under THIS experience: the same service retained elsewhere on the
  // trip, on dates that do price it, says nothing about these ones — and neither
  // does a day service retained on another day of the same program.
  // Before an availability search nothing is held back on prices: no dates, no
  // answer. The status of the service needs no date, and always applies.
  const isOwnSelection = extra =>
    localSelectedExtras.some(
      se =>
        se.id === extra.id &&
        se.experienceId === experience?.id &&
        (se.parentExtraId ?? null) === (parentExtra?.id ?? null) &&
        (serviceDayIndex == null || se.dayIndex === dayIndex),
    );

  const shownExtras = formattedExtras.filter(
    extra =>
      isExtraStateAvailable(extra) &&
      (!availabilitySearched ||
        isExtraOfferedForPeriod(prices, extra.id, serviceDayIndexOf(extra)) ||
        isOwnSelection(extra)),
  );

  const groupedExtras = shownExtras.reduce(
    (acc, extra) => {
      const type = extra.experienceType;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(extra);
      return acc;
    },
    Object.fromEntries(EXTRAS_TABS.map(({ type }) => [type, []])),
  );

  const availableTypes = EXTRAS_TABS.filter(
    ({ type }) => groupedExtras[type]?.length > 0,
  ).map(({ type }) => type);

  const filteredGroupedExtras = shownExtras.filter(matchesFilters).reduce(
    (acc, extra) => {
      const type = extra.experienceType;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(extra);
      return acc;
    },
    Object.fromEntries(EXTRAS_TABS.map(({ type }) => [type, []])),
  );

  // Built from the compatibility-sorted list so that order holds across types,
  // and restricted to the tab types so its count is the sum of the other tabs.
  const allExtras = shownExtras.filter(extra =>
    EXTRAS_TABS.some(({ type }) => type === extra.experienceType),
  );
  const filteredAllExtras = allExtras.filter(matchesFilters);

  const extrasOfTab = tab =>
    tab === ALL_TAB ? filteredAllExtras : filteredGroupedExtras[tab];

  const isFiltering = searchWords.length > 0 || maxDuration < 8;

  useEffect(() => {
    if (availableTypes.length === 0) {
      if (currentTab) {
        setCurrentTab(null);
      }
    } else if (
      !currentTab ||
      (currentTab !== ALL_TAB && groupedExtras[currentTab]?.length === 0)
    ) {
      setCurrentTab(ALL_TAB);
    }
  }, [availableTypes, currentTab, groupedExtras]);

  return (
    <>
      <KoobModal
        open={open}
        setOpen={() => requestClose()}
        allowClose={hasCompletedSelection}
        widthClass="sm:max-w-7xl"
      >
        {/* Full-height column: only the service list scrolls, so the modal
            grows with the viewport instead of being capped at a fixed height. */}
        <div className="flex flex-col max-h-[calc(100vh-10rem)]">
          <div className="px-3 flex-1 min-h-0 overflow-y-auto">
            <ExperienceExtrasModalHeader
              recommendationMode={recommendationMode}
              childrenExtraMode={!!parentExtra}
              currentExperienceName={experience?.name}
              nextExperienceName={nextExperience?.name}
              scope={extrasScope}
            />
            <div className="flex flex-wrap items-center gap-4 mt-6 p-3 border rounded-lg">
              <div className="flex-1 min-w-[14rem]">
                <KoobSearchBar
                  rounded="md"
                  placeholder={t('extras.searchPlaceholder')}
                  value={search ?? ''}
                  onChange={v => {
                    setSearch(v);
                  }}
                  iconRight={
                    search ? <i className="fa-regular fa-xmark" /> : undefined
                  }
                  onIconRightClick={
                    search
                      ? () => {
                          setSearch('');
                        }
                      : undefined
                  }
                  data-testid={`search-input${parentExtra ? '-secondary' : ''}`}
                />
              </div>
              <div className="w-px self-stretch bg-gray-200" />
              <div className="flex items-center gap-3 w-96 shrink-0">
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  {t('extras.durationUpTo')}
                </span>

                <span className="text-xs text-gray-400">1h</span>

                <div className="flex-1">
                  <KoobInputSlider
                    min={1}
                    max={8}
                    step={1}
                    value={[maxDuration]}
                    onValueChange={([value]) => setMaxDuration(value)}
                    showValues={false}
                    data-testid={`duration-slider${parentExtra ? '-secondary' : ''}`}
                  />
                </div>

                <span className="text-xs text-gray-400">8h+</span>

                <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                  {maxDuration >= 8
                    ? t('extras.anyDuration')
                    : `${maxDuration}h`}
                </span>
              </div>
            </div>

            <div className="my-6 flex items-center justify-between gap-4">
              <div className="flex gap-2 overflow-x-auto">
                {availableTypes.length > 0 && (
                  <ExperienceExtraTabButton
                    tab={ALL_TAB}
                    label={`${t('extras.tabs.all')} (${filteredAllExtras.length})`}
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                  />
                )}
                {EXTRAS_TABS.map(({ type }) => {
                  const rawCount = groupedExtras[type]?.length || 0;
                  if (rawCount === 0) return null;
                  const extrasCount = filteredGroupedExtras[type]?.length || 0;
                  const translatedLabel = t(
                    `extras.tabs.${type.toLowerCase()}`,
                    {
                      count: extrasCount,
                    },
                  );
                  return (
                    <ExperienceExtraTabButton
                      key={type}
                      tab={type}
                      label={`${translatedLabel} (${extrasCount})`}
                      currentTab={currentTab}
                      setCurrentTab={setCurrentTab}
                    />
                  );
                })}
              </div>

              <span className="shrink-0 text-sm text-gray-400">
                {t('extras.travellerDefaults', {
                  adults: tripAdults,
                  children: tripChildren,
                })}
              </span>
            </div>

            {availableTypes.length === 0 && (
              <div className="my-8 text-center text-gray-500">
                {t('extras.emptyCatalogue')}
              </div>
            )}

            {currentTab &&
              (extrasOfTab(currentTab)?.length > 0 ? (
                <div className="my-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {extrasOfTab(currentTab).map(
                    ({
                      id,
                      name,
                      type,
                      compatibility,
                      transitionalExperienceId,
                    }) => (
                      <ExperienceExtrasModalListItem
                        key={id}
                        extraId={id}
                        extraName={name}
                        extraType={type}
                        extraScope={extrasScope}
                        compatibility={compatibility}
                        selectedExtras={localSelectedExtras}
                        setSelectedExtras={setLocalSelectedExtras}
                        updateExtrasForDay={handleUpdateExtrasForDay}
                        parentExtra={parentExtra}
                        dayIndex={dayIndex}
                        prices={prices}
                        currency={currency}
                        experience={experience}
                        experienceComposition={experienceComposition}
                        parentOccurrences={parentOccurrences}
                        source={source}
                        disabled={
                          (selectedRequiredChildrenExtras?.length &&
                            type === ExtraRequirementEnum.REQUIRED) ||
                          skipChildrenExtra
                        }
                        readOnly={readOnly}
                        programDayIndex={programDayIndex}
                        recommendationMode={recommendationMode}
                        transitionalExperienceId={transitionalExperienceId}
                        parentTargetDayIndex={parentTargetDayIndex}
                        availabilitySearched={availabilitySearched}
                        serviceDayIndex={serviceDayIndex}
                      />
                    ),
                  )}
                </div>
              ) : (
                isFiltering && (
                  <div className="my-8 flex flex-col items-center gap-3 text-center text-gray-500">
                    <div>{t('extras.noResultsForFilters')}</div>

                    <KoobButton
                      color="secondary"
                      onClick={() => {
                        setSearch('');
                        setMaxDuration(8);
                      }}
                    >
                      {t('extras.resetFilters')}
                    </KoobButton>
                  </div>
                )
              ))}
          </div>

          {recommendationMode && (
            <ExperienceExtrasModalSkipTransitionExtra
              dayIndex={dayIndex}
              onSkip={() => setOpen(false)}
            />
          )}

          {parentExtra && !readOnly && (
            <ExperienceExtrasModalSkipChildrenExtra
              skipChildrenExtra={skipChildrenExtra}
              setSkipChildrenExtra={setSkipChildrenExtra}
              onContinue={() => confirmChoice()}
              disabled={
                selectedRequiredChildrenExtras?.length || hasUnpricedSelection
              }
            />
          )}

          <div className="mt-5 shrink-0 flex items-center space-x-3 justify-end">
            {!parentExtra && (
              <span className="mr-auto text-sm text-gray-500">
                {addedServicesCount === 0
                  ? t('extras.addedRecapEmpty')
                  : t('extras.addedRecap', { count: addedServicesCount })}
              </span>
            )}

            {readOnly && (
              <KoobButton color="secondary" onClick={() => setOpen(false)}>
                {t('misc:close')}
              </KoobButton>
            )}

            {!readOnly && (
              <>
                {!parentExtra ? (
                  <>
                    <KoobButton
                      color={!recommendationMode ? 'secondary' : undefined}
                      onClick={() => requestClose()}
                    >
                      {t('misc:cancel')}
                    </KoobButton>

                    <KoobButton
                      variant="primary"
                      onClick={() => confirmChoice()}
                      disabled={
                        hasUnpricedSelection ||
                        (recommendationMode &&
                          !localSelectedExtras.some(se =>
                            (extras || []).some(ei => ei.id === se.id),
                          ))
                      }
                      testId={'save-primary-options'}
                    >
                      {t('misc:save')}
                    </KoobButton>
                  </>
                ) : (
                  <>
                    <KoobButton
                      color={!recommendationMode ? 'secondary' : undefined}
                      onClick={() => setOpen(false)}
                      disabled={isDisabled}
                    >
                      {t('misc:cancel')}
                    </KoobButton>

                    <KoobButton
                      variant="primary"
                      onClick={() => confirmChoice()}
                      disabled={!hasCompletedSelection || hasUnpricedSelection}
                      testId={'save-secondary-options'}
                    >
                      {t('misc:save')}
                    </KoobButton>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </KoobModal>

      <KoobConfirmationModal
        open={showDiscardConfirm}
        setOpen={setShowDiscardConfirm}
        title={t('extras.discardConfirm.title')}
        description={t('extras.discardConfirm.description')}
        confirmLabel={t('extras.discardConfirm.confirm')}
        cancelLabel={t('extras.discardConfirm.cancel')}
        onConfirm={discardOnClose}
        danger
      />
    </>
  );
}
